<template>
    <div>
        <!-- ── Trigger cuando aún NO hay plazos seleccionados ── -->
        <template v-if="!plazosSeleccionados">
            <div class="tw:mt-4 tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-3 tw:p-4 tw:rounded-xl tw:border tw:border-dashed tw:border-slate-300 tw:bg-slate-50/50">
                <p class="tw:text-sm tw:text-slate-600 tw:m-0 tw:flex-1">
                    ¿Deseas realizar el pago de la base <strong>a plazos</strong>?
                </p>
                <Button
                    customClass="tw:w-full tw:md:w-auto tw:justify-center"
                    :disabled="disabled"
                    @click="openModal"
                >
                    Pagar a plazos
                </Button>
            </div>
        </template>

        <!-- ── Banner cuando SÍ hay plazos seleccionados ── -->
        <template v-else>
            <div class="tw:mt-4 tw:rounded-xl tw:border tw:border-blue-200 tw:bg-blue-50/50 tw:p-4">
                <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                    <svg class="tw:w-5 tw:h-5 tw:text-blue-500 tw:shrink-0 tw:mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <div class="tw:flex-1">
                        <p class="tw:text-sm tw:font-medium tw:text-blue-800 tw:m-0">
                            Pago fraccionado en <strong>{{ data.plazoSelect }}</strong> {{ data.plazoSelect === 1 ? 'plazo' : 'plazos' }}
                            <template v-if="selectedFrecuenciaObj">
                                ({{ selectedFrecuenciaObj.frecuencia_formatted || selectedFrecuenciaObj.nombre }})
                            </template>
                        </p>
                    </div>
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-2 tw:w-full tw:sm:w-auto">
                        <Button
                            customClass="tw:w-full tw:sm:w-auto tw:justify-center tw:text-xs"
                            :disabled="disabled"
                            @click="openModal"
                        >
                            Modificar plazos
                        </Button>
                        <Button
                            customClass="tw:w-full tw:sm:w-auto tw:justify-center tw:text-xs"
                            :disabled="disabled"
                            @click="handleReset"
                        >
                            Pagar importe completo
                        </Button>
                    </div>
                </div>
            </div>

            <!-- Error de fecha límite -->
            <div
                v-if="errorPlazos"
                class="tw:mt-2 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-xs tw:text-red-700 tw:flex tw:items-start tw:gap-2"
            >
                <svg class="tw:w-4 tw:h-4 tw:shrink-0 tw:mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                </svg>
                <span>No es posible fraccionar los plazos de pago con fecha superior a fecha {{ fechaLimitePlazos }}.</span>
            </div>
        </template>

        <!-- ── Modal de selección de plazos (ModalBase con portal, overflow:visible) ── -->
        <ModalBase
            name="addPlazosModal"
            title="Activar pago a plazos"
            subtitle="Selecciona la cantidad de plazos y la frecuencia de pago."
        >
            <div class="tw:space-y-5">
                <div>
                    <BaseMultiselect
                        :value="data.plazoSelect"
                        :options="cuotasOptions"
                        label="Cantidad de plazos"
                        name="num_plazos"
                        rules="required"
                        value-key="value"
                        label-key="label"
                        :multiple="false"
                        @input="val => data.plazoSelect = val"
                    />
                </div>

                <div v-if="frecuencias && frecuencias.length > 0">
                    <BaseMultiselect
                        :value="data.frecuenciaSelect"
                        :options="frecuencias"
                        label="Frecuencia"
                        name="frecuencia"
                        rules="required"
                        value-key="id"
                        label-key="frecuencia_formatted"
                        :multiple="false"
                        @input="val => data.frecuenciaSelect = val"
                    />
                </div>

                <p class="tw:text-xs tw:text-slate-500 tw:italic">
                    *Por cada plazo se cobrará el gasto de gestión correspondiente: 1 €
                </p>
            </div>

            <template #footer>
                <div class="tw:flex tw:items-center tw:justify-end tw:gap-3">
                    <Button
                        variant="ghost"
                        @click="closeModal"
                    >
                        Cancelar
                    </Button>
                    <Button
                        :disabled="!canConfirm"
                        @click="handleConfirm"
                    >
                        Continuar
                    </Button>
                </div>
            </template>
        </ModalBase>
    </div>
</template>

<script setup>
    import { ref, reactive, computed } from 'vue';
    import { useModal } from '@/composables/common/useModal';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import ModalBase from '@/modules/admin/ui/ModalBase.vue';

    const modal = useModal();

    const props = defineProps({
        frecuencias: { type: Array, default: () => [] },
        minPlazos: { type: Number, default: 1 },
        maxPlazos: { type: Number, default: 12 },
        gastos: { type: Number, default: 0 },
        tieneGastos: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        plazosSeleccionados: { type: Boolean, default: false },
        errorPlazos: { type: Boolean, default: false },
        fechaLimitePlazos: { type: String, default: '' },
    });

    const emit = defineEmits(['terminado', 'reset']);

    const data = reactive({
        plazoSelect: null,
        frecuenciaSelect: null,
    });

    const cuotasOptions = computed(() => {
        const options = [];
        for (let i = props.minPlazos; i <= props.maxPlazos; i++) {
            options.push({ value: i, label: `${i} ${i === 1 ? 'plazo' : 'plazos'}` });
        }
        return options;
    });

    /** Objeto de frecuencia seleccionado (para mostrar el nombre formateado) */
    const selectedFrecuenciaObj = computed(() => {
        if (!data.frecuenciaSelect || !props.frecuencias?.length) return null;
        return props.frecuencias.find(f => f.id === data.frecuenciaSelect) || null;
    });

    const canConfirm = computed(() => {
        if (!data.plazoSelect) return false;
        if (props.frecuencias?.length > 0 && !data.frecuenciaSelect) return false;
        return true;
    });

    function openModal() {
        modal.show('addPlazosModal');
    }

    function closeModal() {
        modal.hide('addPlazosModal');
    }

    function handleConfirm() {
        emit('terminado', {
            plazoSelect: data.plazoSelect,
            frecuenciaSelect: data.frecuenciaSelect,
        });
        closeModal();
    }

    function handleReset() {
        data.plazoSelect = null;
        data.frecuenciaSelect = null;
        emit('reset');
    }
</script>
